import { memo, useCallback, useEffect, useRef, useState } from 'react'
import {
  FlatList,
  View,
  Text,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native'
import { BrandedRefreshControl } from '@/components/ui/BrandedRefreshControl'
import { ScrollView } from 'react-native-gesture-handler'
import { useFocusEffect, useRouter } from 'expo-router'
import { useListingWizardGate } from '@/lib/hooks/use-open-listing-wizard'
import { ResumeDraftDialog } from '@/components/hosting/ResumeDraftDialog'
import { useTranslation } from 'react-i18next'
import { StatusBar } from 'expo-status-bar'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { scheduleIdleTask } from '@/lib/utils/idle-callback'
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { LinearGradient } from 'expo-linear-gradient'
import {
  ChevronRight,
  Car,
  Map as MapIcon,
  SearchX,
  SlidersHorizontal,
} from '@/components/ui/icons'
import { Pressable } from '@/components/ui/Pressable'
import { EmptyState } from '@/components/ui/EmptyState'
import { CategoryFilter } from '@/components/explore/CategoryFilter'
import { VehicleCard } from '@/components/explore/VehicleCard'
import { VehicleListItem } from '@/components/explore/VehicleListItem'
import { SearchBlock } from '@/components/explore/SearchBlock'
import { MapPreview } from '@/components/explore/MapPreview'
import type { MapInitialFrame } from '@/lib/maps/mapbox-standard'
import { shouldArmDeferredNativeMap } from '@/lib/maps/native-map-lifecycle'
import { FilterSheet } from '@/components/explore/FilterSheet'
import { LocationSearchModal } from '@/components/explore/LocationSearchModal'
import { useVehicles } from '@/lib/hooks/use-vehicles'
import { useLocation } from '@/lib/hooks/use-location'
import { useBreakpoint } from '@/lib/hooks/use-breakpoint'
import { useFontScale } from '@/lib/hooks/use-font-scale'
import { useExploreStore } from '@/lib/stores/explore.store'
import { useSemanticColors, DURATION, INTERACTION, STAGGER } from '@/lib/theme/tokens'
import { WEB_SHADOWS } from '@/lib/theme/web-shadow'
import { COLORS, FONTS, SPACING, RADIUS } from '@/lib/brand'
import { useTheme } from '@/lib/hooks/use-theme'
import { isIndependenceCampaignBookingWindowOpen } from '@/lib/utils/pricing-core'
import { useProductTelemetryScroll } from '@/lib/hooks/use-product-telemetry-scroll'
import { productTelemetryService } from '@/lib/services/product-telemetry.service'
import type { SearchVehicle } from '@/lib/types/vehicle.types'
import { LiquidCanvas } from '@/components/ui/LiquidBackdrop'

const DEFAULT_EXPLORE_MAP_PREVIEW_HEIGHT = 240
const EXPLORE_MOBILE_MAP_ASPECT_RATIO = 0.64
const EXPLORE_TABLET_MAP_ASPECT_RATIO = 0.42
const EXPLORE_MOBILE_MAP_MIN_HEIGHT = 206
const EXPLORE_TABLET_MAP_MIN_HEIGHT = 260
const EXPLORE_TABLET_MAP_MAX_HEIGHT = 320
const EXPLORE_MOBILE_CARD_WIDTH_RATIO = 0.65
const EXPLORE_MOBILE_CARD_MIN_WIDTH = 220
const EXPLORE_MOBILE_CARD_MAX_WIDTH = 230
const NEARBY_CAROUSEL_GAP = SPACING.sm + SPACING.xs
const NEARBY_CAROUSEL_AUTO_ADVANCE_MS = 5200
const NEARBY_CAROUSEL_INTERACTION_PAUSE_MS = 9000
const NEARBY_CAROUSEL_MAX_DOTS = 5
// Never wider than a card whose 220pt media frame can still hold a 3:2 photo
// without letterboxing it. Matches the tile width the "Ver todos" grid caps at.
const EXPLORE_TABLET_TILE_MAX_WIDTH = 360
// Above this width the grid goes to three columns.
//
// Two columns capped at 360 left a quarter of an iPad Pro empty: measured at
// 1023pt wide the cards ended at x=764 and 259pt of nothing followed them. The
// answer to a wider screen is more items, not fatter ones — stretching two
// cards to 475pt each would have been the zoomed-phone look this is meant to
// avoid.
//
// 950 is where three columns stop being cramped: (950 - 56 padding - 32 gaps)/3
// is 287pt, still wider than the 220pt card the phone carousel ships. Below it
// an iPad Air at 834 would land on 249pt tiles, narrower than the phone's, so
// it stays on two.
const EXPLORE_THREE_COLUMN_MIN_WIDTH = 950
// Explicit so the "Ver todos" link can be centred on the title's own line box
// rather than on the two-line heading block it sits beside.
const SECTION_TITLE_LINE_HEIGHT = { mobile: 22, desktop: 26 } as const
const EXPLORE_TABLET_TILE_STYLE = {
  flexBasis: '48%' as const,
  flexGrow: 1,
  maxWidth: EXPLORE_TABLET_TILE_MAX_WIDTH,
}
const EXPLORE_TABLET_TILE_STYLE_WIDE = {
  flexBasis: '31%' as const,
  flexGrow: 1,
  maxWidth: EXPLORE_TABLET_TILE_MAX_WIDTH,
}

/** Two columns, or three once the screen is wide enough to hold them. */
function tabletTileStyle(width: number) {
  return width >= EXPLORE_THREE_COLUMN_MIN_WIDTH
    ? EXPLORE_TABLET_TILE_STYLE_WIDE
    : EXPLORE_TABLET_TILE_STYLE
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

function SectionHeader({
  title,
  subtitle,
  subtitleTestID,
  onViewAll,
  colors,
  desktop,
}: {
  title: string
  subtitle?: string
  subtitleTestID?: string
  onViewAll: () => void
  colors: ReturnType<typeof useSemanticColors>
  desktop?: boolean
}) {
  const { t } = useTranslation()
  const titleLineHeight = desktop
    ? SECTION_TITLE_LINE_HEIGHT.desktop
    : SECTION_TITLE_LINE_HEIGHT.mobile
  return (
    <View
      style={{
        flexDirection: 'row',
        justifyContent: 'space-between',
        // Top-aligned, not centred. Centred against a two-line heading block
        // ("Cerca de vos" over "22 disponibles") the link floated in the gap
        // between the two lines, sharing a baseline with neither.
        alignItems: 'flex-start',
        gap: SPACING.md,
        marginBottom: desktop ? SPACING.md : SPACING.sm + 2,
      }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            fontSize: desktop ? 20 : 17,
            lineHeight: titleLineHeight,
            color: colors.textPrimary,
            fontFamily: FONTS.bodyBold,
            letterSpacing: -0.4,
          }}
        >
          {title}
        </Text>
        {subtitle ? (
          <Text
            testID={subtitleTestID}
            style={{
              fontSize: desktop ? 13 : 12,
              color: colors.textSecondary,
              marginTop: 2,
              fontFamily: FONTS.body,
            }}
          >
            {subtitle}
          </Text>
        ) : null}
      </View>
      {/* A bare text link is a 15px-tall target. The row keeps its visual
          rhythm via negative margin while the touchable box reaches 44.
          The negative top margin re-centres that 44pt box on the TITLE's line
          box instead of on the whole heading block, so link and title share a
          baseline and the subtitle hangs below both. */}
      <Pressable
        onPress={onViewAll}
        accessibilityRole="button"
        accessibilityLabel={t('explore.view_all')}
        testID="explore.view-all"
        hitSlop={8}
        style={{
          minHeight: INTERACTION.minTouchTarget,
          justifyContent: 'center',
          paddingHorizontal: SPACING.xs,
          marginRight: -SPACING.xs,
          marginTop: (titleLineHeight - INTERACTION.minTouchTarget) / 2,
        }}
      >
        <Text
          style={{
            fontSize: desktop ? 13 : 12,
            color: colors.textBrand,
            fontFamily: FONTS.body,
          }}
        >
          {t('explore.view_all')}
        </Text>
      </Pressable>
    </View>
  )
}

function SalvadorFlagMark({ compact = false }: { compact?: boolean }) {
  const width = compact ? 58 : 68
  const height = compact ? 36 : 42
  const stripeHeight = height / 3

  return (
    <View
      pointerEvents="none"
      style={{
        width,
        height,
        borderRadius: RADIUS.sm,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: `${COLORS.flag.blue}33`,
        backgroundColor: COLORS.white,
      }}
    >
      <View style={{ height: stripeHeight, backgroundColor: COLORS.flag.blue }} />
      <View
        style={{
          height: stripeHeight,
          backgroundColor: COLORS.white,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <View
          style={{
            width: compact ? 10 : 12,
            height: compact ? 10 : 12,
            borderRadius: RADIUS.full,
            borderWidth: 1.5,
            borderColor: COLORS.flag.gold,
            backgroundColor: `${COLORS.flag.gold}24`,
          }}
        />
      </View>
      <View style={{ height: stripeHeight, backgroundColor: COLORS.flag.blue }} />
    </View>
  )
}

function IndependenceCampaignBanner({
  onPress,
  desktop,
}: {
  onPress: () => void
  desktop?: boolean
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const bannerBackground = isDark ? colors.surfaceRaised : COLORS.white
  const patrioticAccent = isDark ? COLORS.flag.blueSoft : COLORS.flag.blue

  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={t('explore.independence_banner_accessibility')}
      testID="explore.independence-banner"
      style={{
        minHeight: desktop ? 104 : 112,
        borderRadius: RADIUS.sm,
        overflow: 'hidden',
        backgroundColor: bannerBackground,
        borderWidth: 1,
        borderColor: isDark ? `${COLORS.white}1A` : `${COLORS.flag.blue}1F`,
        ...WEB_SHADOWS.sm,
      }}
    >
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          left: 0,
          height: 4,
          flexDirection: 'row',
        }}
      >
        <View style={{ flex: 1, backgroundColor: COLORS.flag.blue }} />
        <View style={{ flex: 1, backgroundColor: COLORS.white }} />
        <View style={{ flex: 1, backgroundColor: COLORS.flag.blue }} />
      </View>

      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.sm + SPACING.xs,
          padding: desktop ? SPACING.md : SPACING.md,
          paddingTop: desktop ? SPACING.md + SPACING.xs : SPACING.md + SPACING.xs,
        }}
      >
        <SalvadorFlagMark compact={!desktop} />
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            numberOfLines={1}
            style={{
              fontSize: 12,
              lineHeight: 16,
              color: patrioticAccent,
              fontFamily: FONTS.body,
              textTransform: 'uppercase',
            }}
          >
            {t('explore.independence_banner_kicker')}
          </Text>
          <Text
            numberOfLines={2}
            style={{
              marginTop: 3,
              fontSize: desktop ? 17 : 16,
              lineHeight: desktop ? 22 : 21,
              color: colors.textPrimary,
              fontFamily: FONTS.bodyBold,
            }}
          >
            {t('explore.independence_banner_title')}
          </Text>
          <Text
            numberOfLines={2}
            style={{
              marginTop: 4,
              fontSize: desktop ? 13 : 12,
              lineHeight: desktop ? 18 : 17,
              color: colors.textSecondary,
              fontFamily: FONTS.body,
            }}
          >
            {t('explore.independence_banner_body')}
          </Text>
        </View>
        <View
          style={{
            width: 38,
            height: 38,
            borderRadius: RADIUS.sm,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isDark ? `${COLORS.white}14` : `${COLORS.flag.blue}14`,
            flexShrink: 0,
          }}
        >
          <ChevronRight size={18} color={patrioticAccent} strokeWidth={2.4} />
        </View>
      </View>
    </Pressable>
  )
}

function ExploreNoResultsPanel({
  colors,
  onFilterPress,
}: {
  colors: ReturnType<typeof useSemanticColors>
  onFilterPress: () => void
}) {
  const { t } = useTranslation()

  return (
    <LinearGradient
      colors={[COLORS.purple.dark, COLORS.purple.DEFAULT, COLORS.coral.DEFAULT]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={{
        borderRadius: RADIUS.lg,
        padding: SPACING.lg,
        overflow: 'hidden',
        ...WEB_SHADOWS.md,
      }}
    >
      <View
        pointerEvents="none"
        style={{
          position: 'absolute',
          top: -28,
          right: -48,
          width: 190,
          height: 120,
          borderRadius: RADIUS.xl,
          borderWidth: 1,
          borderColor: 'rgba(255,255,255,0.14)',
          transform: [{ rotate: '-12deg' }],
        }}
      />
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.md,
        }}
      >
        <View
          style={{
            width: 54,
            height: 54,
            borderRadius: RADIUS.lg,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.14)',
            borderWidth: 1,
            borderColor: 'rgba(255,255,255,0.20)',
          }}
        >
          <SearchX size={24} color="#FFFFFF" strokeWidth={2} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text
            style={{
              fontSize: 16,
              lineHeight: 22,
              color: COLORS.white,
              fontFamily: FONTS.bodyBold,
            }}
          >
            {t('explore.no_results')}
          </Text>
          <Text
            style={{
              fontSize: 13,
              lineHeight: 19,
              color: 'rgba(255,255,255,0.78)',
              fontFamily: FONTS.body,
              marginTop: 2,
            }}
            numberOfLines={2}
          >
            {t('explore.nearby_empty')}
          </Text>
        </View>
      </View>
      <Pressable
        onPress={onFilterPress}
        accessibilityRole="button"
        accessibilityLabel={t('explore.filter_title')}
        testID="explore.no-results-filter-cta"
        style={{
          marginTop: SPACING.md,
          alignSelf: 'flex-start',
          flexDirection: 'row',
          alignItems: 'center',
          gap: SPACING.xs,
          paddingHorizontal: SPACING.md,
          paddingVertical: SPACING.sm,
          borderRadius: RADIUS.full,
          backgroundColor: colors.surface,
        }}
      >
        <SlidersHorizontal size={16} color={COLORS.purple.DEFAULT} strokeWidth={2} />
        <Text
          style={{
            fontSize: 13,
            color: colors.textBrand,
            fontFamily: FONTS.bodyBold,
          }}
        >
          {t('explore.filter_title')}
        </Text>
      </Pressable>
    </LinearGradient>
  )
}

const ExploreVehicleCardTile = memo(function ExploreVehicleCardTile({
  vehicle,
  index,
  isFavorite,
  onVehiclePress,
  onToggleFavorite,
  desktop,
  mobileWidth,
  viewportWidth,
}: {
  vehicle: SearchVehicle
  index: number
  isFavorite: boolean
  onVehiclePress: (vehicle: SearchVehicle) => void
  onToggleFavorite: (vehicleId: string) => void
  desktop?: boolean
  mobileWidth?: number
  viewportWidth?: number
}) {
  const handlePress = useCallback(() => onVehiclePress(vehicle), [onVehiclePress, vehicle])
  const handleFavorite = useCallback(
    () => onToggleFavorite(vehicle.id),
    [onToggleFavorite, vehicle.id],
  )

  return (
    <View
      key={vehicle.id}
      testID={`explore.vehicle-card-${index}`}
      style={desktop ? tabletTileStyle(viewportWidth ?? 0) : undefined}
    >
      <VehicleCard
        vehicle={vehicle}
        isFavorite={isFavorite}
        onPress={handlePress}
        onToggleFavorite={handleFavorite}
        desktop={desktop}
        mobileWidth={mobileWidth}
      />
    </View>
  )
})

const ExploreVehicleListTile = memo(function ExploreVehicleListTile({
  vehicle,
  index,
  onVehiclePress,
  desktop,
  testPrefix = 'explore.vehicle-card',
}: {
  vehicle: SearchVehicle
  index: number
  onVehiclePress: (vehicle: SearchVehicle) => void
  desktop?: boolean
  testPrefix?: string
}) {
  const handlePress = useCallback(() => onVehiclePress(vehicle), [onVehiclePress, vehicle])

  return (
    <View key={vehicle.id} testID={`${testPrefix}-${index}`}>
      <VehicleListItem vehicle={vehicle} onPress={handlePress} desktop={desktop} />
    </View>
  )
})

/**
 * Separador de la FlatList del carrusel. Antes el gap vivía en el
 * `contentContainerStyle` del ScrollView; con virtualización el offset de cada
 * card es `índice * (ancho + NEARBY_CAROUSEL_GAP)`, así que el hueco tiene que
 * medir exactamente lo mismo que `snapToInterval`.
 */
function NearbyCarouselGap() {
  return <View style={{ width: NEARBY_CAROUSEL_GAP }} />
}

function NearbyVehicleCarousel({
  vehicles,
  favoriteIds,
  mobileCardWidth,
  contentPadding,
  onVehiclePress,
  onToggleFavorite,
}: {
  vehicles: SearchVehicle[]
  favoriteIds: Set<string>
  mobileCardWidth: number
  /**
   * Horizontal content margin of the screen. The scroller cancels it and
   * re-applies it inside, so cards are clipped at the VIEWPORT edge — the
   * same edge rule the filter chip row above already uses — instead of
   * stopping short on the content margin while the chips ran off the screen.
   * Scroll offsets are unaffected: the padding shifts the whole track, so
   * `snapToInterval` still lands every card on the content margin.
   */
  contentPadding: number
  onVehiclePress: (vehicle: SearchVehicle) => void
  onToggleFavorite: (vehicleId: string) => void
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const reducedMotion = useReducedMotion()
  const scrollRef = useRef<FlatList<SearchVehicle>>(null)
  const activeIndexRef = useRef(0)
  const pauseUntilRef = useRef(0)
  const [activeIndex, setActiveIndex] = useState(0)

  const vehicleCount = vehicles.length
  const lastIndex = Math.max(vehicleCount - 1, 0)
  const nearbyCarouselStep = mobileCardWidth + NEARBY_CAROUSEL_GAP
  const canAutoAdvance = vehicleCount > 1 && !reducedMotion
  const visibleDotCount = Math.min(vehicleCount, NEARBY_CAROUSEL_MAX_DOTS)
  const firstVisibleDot = clamp(
    activeIndex - Math.floor(NEARBY_CAROUSEL_MAX_DOTS / 2),
    0,
    Math.max(vehicleCount - NEARBY_CAROUSEL_MAX_DOTS, 0),
  )
  const visibleDotIndices = Array.from(
    { length: visibleDotCount },
    (_, offset) => firstVisibleDot + offset,
  )

  const syncActiveIndex = useCallback(
    (nextIndex: number) => {
      const next = clamp(nextIndex, 0, lastIndex)
      if (next === activeIndexRef.current) return
      activeIndexRef.current = next
      setActiveIndex(next)
    },
    [lastIndex],
  )

  const pauseAutoAdvanceTemporarily = useCallback(() => {
    pauseUntilRef.current = Date.now() + NEARBY_CAROUSEL_INTERACTION_PAUSE_MS
  }, [])

  const scrollToIndex = useCallback(
    (nextIndex: number, animated = true) => {
      const next = clamp(nextIndex, 0, lastIndex)
      activeIndexRef.current = next
      setActiveIndex(next)
      // FlatList: el offset usa el MISMO paso que `snapToInterval` (ancho de
      // card + gap), así el snap, los dots y el auto-advance aterrizan igual
      // que con el ScrollView anterior.
      scrollRef.current?.scrollToOffset({
        offset: next * nearbyCarouselStep,
        animated,
      })
    },
    [lastIndex, nearbyCarouselStep],
  )

  const handleScroll = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      const next = Math.round(event.nativeEvent.contentOffset.x / nearbyCarouselStep)
      syncActiveIndex(next)
    },
    [nearbyCarouselStep, syncActiveIndex],
  )

  const handleDotPress = useCallback(
    (nextIndex: number) => {
      pauseAutoAdvanceTemporarily()
      scrollToIndex(nextIndex)
    },
    [pauseAutoAdvanceTemporarily, scrollToIndex],
  )

  useEffect(() => {
    const next = clamp(activeIndexRef.current, 0, lastIndex)
    activeIndexRef.current = next
    setActiveIndex(next)
    scrollRef.current?.scrollToOffset({ offset: next * nearbyCarouselStep, animated: false })
  }, [lastIndex, nearbyCarouselStep])

  useEffect(() => {
    if (!canAutoAdvance) return

    const interval = setInterval(() => {
      if (Date.now() < pauseUntilRef.current) return
      const next = activeIndexRef.current >= lastIndex ? 0 : activeIndexRef.current + 1
      scrollToIndex(next)
    }, NEARBY_CAROUSEL_AUTO_ADVANCE_MS)

    return () => clearInterval(interval)
  }, [canAutoAdvance, lastIndex, scrollToIndex])

  return (
    <View testID="explore.nearby-carousel">
      {/*
        Virtualizada (TAGO-MOBILE-5K / 5J). Antes era un ScrollView con
        `vehicles.map`: las 60 cards —cada una con su árbol de vistas e imagen—
        se montaban en UNA sola transacción. En un iPhone 12 eso superaba los
        2 s (el watchdog de Sentry lo capturó dentro de
        `updateClippedSubviewsWithClipRect`) y además disparaba el setImage de
        todas las expo-image a la vez. La FlatList monta ~3 cards y el resto
        entra por ventana; el snap, los dots y el auto-advance no cambian.
      */}
      <FlatList
        ref={scrollRef}
        horizontal
        data={vehicles}
        keyExtractor={(vehicle) => vehicle.id}
        renderItem={({ item, index }) => (
          <ExploreVehicleCardTile
            vehicle={item}
            index={index}
            isFavorite={favoriteIds.has(item.id)}
            onVehiclePress={onVehiclePress}
            onToggleFavorite={onToggleFavorite}
            mobileWidth={mobileCardWidth}
          />
        )}
        ItemSeparatorComponent={NearbyCarouselGap}
        initialNumToRender={3}
        maxToRenderPerBatch={4}
        windowSize={5}
        showsHorizontalScrollIndicator={false}
        snapToInterval={nearbyCarouselStep}
        snapToAlignment="start"
        decelerationRate="fast"
        disableIntervalMomentum
        scrollEventThrottle={16}
        onScroll={handleScroll}
        onScrollBeginDrag={pauseAutoAdvanceTemporarily}
        onMomentumScrollEnd={handleScroll}
        testID="explore.nearby-carousel-scroll"
        style={{ marginHorizontal: -contentPadding }}
        contentContainerStyle={{
          paddingHorizontal: contentPadding,
          paddingBottom: SPACING.xs,
        }}
      />

      {vehicleCount > 1 ? (
        <View
          testID="explore.nearby-carousel-page-control"
          // Each dot owns a full 44x44 target, so the row is exactly one
          // touch-target tall and needs no extra top margin of its own.
          style={{
            minHeight: INTERACTION.minTouchTarget,
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {visibleDotIndices.map((dotIndex) => {
            const selected = dotIndex === activeIndex
            const isEdgeDot =
              vehicleCount > NEARBY_CAROUSEL_MAX_DOTS &&
              (dotIndex === firstVisibleDot ||
                dotIndex === firstVisibleDot + visibleDotCount - 1) &&
              !selected

            return (
              <Pressable
                key={dotIndex}
                onPress={() => handleDotPress(dotIndex)}
                accessibilityRole="button"
                accessibilityLabel={t('explore.nearby_carousel_dot', {
                  index: dotIndex + 1,
                  total: vehicleCount,
                })}
                accessibilityState={{ selected }}
                testID={`explore.nearby-carousel-dot-${dotIndex}`}
                // The pill stays 5pt tall; only the invisible target grows to
                // the 44x44 minimum. hitSlop is not enough here — react-native-web
                // drops it, and this carousel is the mobile-web layout.
                style={{
                  width: INTERACTION.minTouchTarget,
                  height: INTERACTION.minTouchTarget,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <View
                  style={{
                    width: selected ? 13 : isEdgeDot ? 4 : 5,
                    height: selected ? 5 : isEdgeDot ? 4 : 5,
                    borderRadius: RADIUS.full,
                    backgroundColor: selected
                      ? COLORS.purple.DEFAULT
                      : isDark
                        ? 'rgba(255,255,255,0.22)'
                        : 'rgba(72,12,130,0.16)',
                  }}
                />
              </Pressable>
            )
          })}
        </View>
      ) : null}
    </View>
  )
}

function DeferredMapPreview({
  vehicles,
  location,
  onViewMap,
  onVehiclePress,
  height,
  initialFrame,
}: {
  vehicles: SearchVehicle[]
  location: ReturnType<typeof useLocation>['location']
  onViewMap: () => void
  onVehiclePress: (id: string) => void
  height?: number
  initialFrame: MapInitialFrame
}) {
  const { t } = useTranslation()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const [ready, setReady] = useState(false)
  const mapHeight = height ?? DEFAULT_EXPLORE_MAP_PREVIEW_HEIGHT

  useFocusEffect(
    useCallback(() => {
      let cancelled = false
      let timeout: ReturnType<typeof setTimeout> | undefined
      const cancelIdleTask = scheduleIdleTask(() => {
        if (!shouldArmDeferredNativeMap({ screenFocused: true, cancelled })) return
        timeout = setTimeout(() => {
          if (shouldArmDeferredNativeMap({ screenFocused: true, cancelled })) setReady(true)
        }, 120)
      })

      return () => {
        cancelled = true
        cancelIdleTask()
        if (timeout) clearTimeout(timeout)
        setReady(false)
      }
    }, []),
  )

  if (ready) {
    return (
      <MapPreview
        vehicles={vehicles}
        userLocation={location}
        onViewMap={onViewMap}
        onVehiclePress={onVehiclePress}
        height={height}
        compactPreview
        initialFrame={initialFrame}
      />
    )
  }

  return (
    <Pressable
      onPress={onViewMap}
      accessibilityRole="button"
      accessibilityLabel={t('explore.view_map')}
      testID="explore.map-preview-placeholder"
      style={{
        height: mapHeight,
        borderRadius: RADIUS.lg,
        overflow: 'hidden',
        alignItems: 'center',
        justifyContent: 'center',
        borderWidth: 1.5,
        borderColor: colors.border,
        backgroundColor: isDark ? colors.surface : '#F7F5FA',
        ...WEB_SHADOWS.sm,
      }}
    >
      <LinearGradient
        pointerEvents="none"
        colors={
          isDark
            ? ['rgba(91,84,144,0.28)', 'rgba(239,78,74,0.10)']
            : ['rgba(91,84,144,0.10)', 'rgba(239,78,74,0.08)']
        }
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={{ position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 }}
      />
      <MapIcon size={28} color={COLORS.purple.DEFAULT} strokeWidth={1.7} />
    </Pressable>
  )
}

// ─── DESKTOP LAYOUT: Airbnb-style split screen ──────────────────────────────

function DesktopExploreLayout({
  vehicles,
  usingCountryFallback,
  isLoading,
  isRefetching,
  isSearching,
  isError,
  refetch,
  handleVehiclePress,
  category,
  setCategory,
  location,
  pad,
  onLocationPress,
  onSearchSubmit,
  onFilterPress,
  activeFilterCount,
  query,
  initialFrame,
}: {
  vehicles: SearchVehicle[]
  /** The nearby search came back empty and the country-wide list is standing in. */
  usingCountryFallback: boolean
  isLoading: boolean
  isRefetching: boolean
  /** Any fetch in flight, including a manual re-search over existing results. */
  isSearching: boolean
  /** The request failed and there is nothing cached to show. */
  isError: boolean
  refetch: () => void
  handleVehiclePress: (v: SearchVehicle) => void
  onLocationPress: () => void
  onSearchSubmit: (query: string) => void
  onFilterPress: () => void
  activeFilterCount: number
  category: ReturnType<typeof useExploreStore.getState>['category']
  setCategory: ReturnType<typeof useExploreStore.getState>['setCategory']
  location: ReturnType<typeof useLocation>['location']
  pad: number
  /** Committed free-text query shown in the search bar (edited in a draft). */
  query: string
  initialFrame: MapInitialFrame
}) {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const { width } = useBreakpoint()
  const sidebarWidth = width >= 1600 ? 560 : width >= 1280 ? 520 : 460
  const locationScope = useExploreStore((state) => state.locationScope)
  // Same honesty rule as the phone layout: a country-wide fallback must not
  // sit under a "Cerca de vos" heading.
  const scopeTitleKey =
    locationScope === 'nearby' && !usingCountryFallback
      ? 'explore.nearby_title'
      : 'explore.all_title'
  const resultSubtitle = (count: number) =>
    usingCountryFallback
      ? t('explore.nearby_fallback_notice')
      : t('explore.available_count', { count })
  const handleOpenAllVehicles = useCallback(() => router.push('/explore/all-vehicles'), [router])
  const handleOpenMap = useCallback(() => {
    productTelemetryService.recordEvent('search', 'search.map_opened', {
      elementId: 'explore.map-preview',
      elementType: 'map_preview',
      metadata: { resultCount: vehicles.length, surface: 'desktop' },
    })
    router.push('/(tabs)/explore/map' as never)
  }, [router, vehicles.length])
  const handleVehicleIdPress = useCallback(
    (id: string) => handleVehiclePress({ id } as SearchVehicle),
    [handleVehiclePress],
  )
  const wizardGate = useListingWizardGate()
  const handleHostCtaPress = wizardGate.open
  const handleDesktopScroll = useProductTelemetryScroll('explore.desktop-list.scroll')
  const showIndependenceCampaign = isIndependenceCampaignBookingWindowOpen()

  return (
    <View
      style={{
        flex: 1,
        width: '100%',
        alignSelf: 'center',
        flexDirection: 'row',
        backgroundColor: colors.background,
      }}
    >
      {/* ── LEFT PANEL: Search + Listings (scrollable) ── */}
      <View
        style={{
          width: sidebarWidth,
          minWidth: 440,
          flexShrink: 0,
          backgroundColor: isDark ? colors.background : '#FBFAFD',
          borderRightWidth: 1,
          borderRightColor: colors.border,
        }}
      >
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          onScroll={handleDesktopScroll}
          scrollEventThrottle={64}
          contentContainerStyle={{
            paddingHorizontal: pad,
            paddingTop: SPACING.lg,
            paddingBottom: SPACING.xl,
            width: '100%',
          }}
          refreshControl={<BrandedRefreshControl refreshing={isRefetching} onRefresh={refetch} />}
        >
          {/* Search Block */}
          <View testID="explore.search-block">
            <SearchBlock
              location={location}
              onLocationPress={onLocationPress}
              onSubmit={onSearchSubmit}
              resultCount={vehicles.length}
              isLoading={isSearching}
              variant="desktop"
              query={query}
            />
          </View>

          {showIndependenceCampaign ? (
            <View style={{ marginTop: SPACING.md }}>
              <IndependenceCampaignBanner onPress={handleOpenAllVehicles} desktop />
            </View>
          ) : null}

          {/* Categories */}
          <View testID="explore.category-filter" style={{ marginTop: SPACING.md }}>
            <CategoryFilter
              selected={category}
              onSelect={setCategory}
              desktop
              onFilterPress={onFilterPress}
              activeFilterCount={activeFilterCount}
            />
          </View>

          {/* Vehicle Cards */}
          <View style={{ marginTop: SPACING.lg - SPACING.xs }}>
            <SectionHeader
              title={t(scopeTitleKey)}
              subtitle={isError ? undefined : resultSubtitle(vehicles.length)}
              subtitleTestID="explore.result-status"
              onViewAll={handleOpenAllVehicles}
              colors={colors}
              desktop
            />
            {isLoading ? (
              <View style={{ gap: SPACING.sm + SPACING.xs }}>
                <VehicleListItem.Skeleton desktop />
                <VehicleListItem.Skeleton desktop />
                <VehicleListItem.Skeleton desktop />
                <VehicleListItem.Skeleton desktop />
              </View>
            ) : isError ? (
              // A failed request is not an empty market — it must say so and
              // offer a retry, without tearing down the search chrome above it.
              <EmptyState.OfflineError onRetry={refetch} />
            ) : vehicles.length === 0 ? (
              <ExploreNoResultsPanel colors={colors} onFilterPress={onFilterPress} />
            ) : (
              <View style={{ gap: SPACING.sm + SPACING.xs }}>
                {vehicles.slice(0, 8).map((v, index) => (
                  <ExploreVehicleListTile
                    key={v.id}
                    vehicle={v}
                    index={index}
                    onVehiclePress={handleVehiclePress}
                    desktop
                  />
                ))}
              </View>
            )}
          </View>

          <ResumeDraftDialog {...wizardGate.dialogProps} testIDPrefix="explore" />

          {/* Host CTA */}
          <Pressable
            onPress={handleHostCtaPress}
            accessibilityRole="button"
            accessibilityLabel={t('explore.host_cta_title')}
            testID="explore.host-cta"
            style={{
              marginTop: SPACING.md,
              padding: SPACING.md,
              borderRadius: RADIUS.lg,
              backgroundColor: colors.surface,
              flexDirection: 'row',
              alignItems: 'center',
              gap: SPACING.sm + SPACING.xs,
              ...WEB_SHADOWS.md,
            }}
          >
            <LinearGradient
              colors={[COLORS.purple.DEFAULT, COLORS.amber.DEFAULT]}
              style={{
                width: 42,
                height: 42,
                borderRadius: RADIUS.md + 1,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Car size={22} color="#FFFFFF" strokeWidth={1.8} />
            </LinearGradient>
            <View style={{ flex: 1 }}>
              <Text style={{ fontSize: 14, color: colors.textPrimary, fontFamily: FONTS.body }}>
                {t('explore.host_cta_title')}
              </Text>
              <Text
                style={{
                  fontSize: 13,
                  color: colors.textSecondary,
                  marginTop: 2,
                  fontFamily: FONTS.body,
                }}
              >
                {t('explore.host_cta_subtitle')}
              </Text>
            </View>
            <ChevronRight size={14} color={COLORS.purple.DEFAULT} strokeWidth={2.5} />
          </Pressable>
        </ScrollView>
      </View>

      {/* ── RIGHT PANEL: Map (sticky, full height) ── */}
      <View
        style={{
          flex: 1,
          minWidth: 0,
          padding: SPACING.lg,
          backgroundColor: colors.background,
        }}
      >
        <View
          collapsable={false}
          testID="explore.map-preview"
          style={{
            flex: 1,
            minHeight: 0,
            borderRadius: RADIUS.xl,
            overflow: 'hidden',
            borderWidth: 1,
            borderColor: colors.border,
            ...WEB_SHADOWS.lg,
          }}
        >
          <MapPreview
            vehicles={vehicles}
            userLocation={location}
            onViewMap={handleOpenMap}
            onVehiclePress={handleVehicleIdPress}
            desktop
            initialFrame={initialFrame}
          />
        </View>
      </View>
    </View>
  )
}

// ─── MAIN COMPONENT ─────────────────────────────────────────────────────────

export default function ExploreScreen() {
  const { t } = useTranslation()
  const router = useRouter()
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const insets = useSafeAreaInsets()
  const { isDesktop, isTablet, width } = useBreakpoint()
  const { box } = useFontScale()
  const { location } = useLocation()
  // The heading names the scope on screen. It said "Cerca de vos" over a
  // country-wide list, which is the same lie the old default made in code.
  const locationScope = useExploreStore((state) => state.locationScope)
  const { category, setCategory, setFilterSheetOpen, query, setQuery, pickupDate, returnDate } =
    useExploreStore()
  const {
    vehicles,
    popularVehicles,
    favoriteIds,
    toggleFavorite,
    usingCountryFallback,
    isLoading,
    isFetching,
    isRefetching,
    error,
    refetch,
    activeFilterCount,
  } = useVehicles()
  const mapInitialFrame: MapInitialFrame =
    locationScope === 'nearby' ||
    Boolean(category) ||
    Boolean(query.trim()) ||
    pickupDate !== null ||
    returnDate !== null ||
    activeFilterCount > 0
      ? 'results'
      : 'default-department'
  // When the 50 km search around the person found nothing and the hook fell
  // back to the whole country, the section must SAY so. Keeping the "Cerca de
  // vos" title over a country-wide list would be the same lie the old
  // hardcoded-San-Salvador default told, wearing better data.
  const scopeTitleKey =
    locationScope === 'nearby' && !usingCountryFallback
      ? 'explore.nearby_title'
      : 'explore.all_title'
  const resultSubtitle = (count: number) =>
    usingCountryFallback
      ? t('explore.nearby_fallback_notice')
      : t('explore.available_count', { count })

  const pad = isDesktop ? 24 : isTablet ? 28 : 20
  const maxW = 1440
  const exploreContentWidth = Math.max(0, Math.min(width, maxW) - pad * 2)
  const exploreMapPreviewHeight = isTablet
    ? clamp(
        Math.round(exploreContentWidth * EXPLORE_TABLET_MAP_ASPECT_RATIO),
        EXPLORE_TABLET_MAP_MIN_HEIGHT,
        EXPLORE_TABLET_MAP_MAX_HEIGHT,
      )
    : clamp(
        Math.round(exploreContentWidth * EXPLORE_MOBILE_MAP_ASPECT_RATIO),
        EXPLORE_MOBILE_MAP_MIN_HEIGHT,
        DEFAULT_EXPLORE_MAP_PREVIEW_HEIGHT,
      )
  // The card is a fixed 220-230dp box holding a title, a rating line and a
  // price. Its type scales with the OS text setting and its width did not, so
  // at 2x everything inside it truncated at once. The card grows with the
  // text instead — up to the full content width, past which a horizontal
  // carousel of one card is just a list.
  const exploreVehicleCardWidth = isTablet
    ? undefined
    : clamp(
        Math.round(exploreContentWidth * EXPLORE_MOBILE_CARD_WIDTH_RATIO),
        Math.min(box(EXPLORE_MOBILE_CARD_MIN_WIDTH), exploreContentWidth),
        Math.min(box(EXPLORE_MOBILE_CARD_MAX_WIDTH), exploreContentWidth),
      )

  const [locationModalVisible, setLocationModalVisible] = useState(false)

  const handleVehiclePress = useCallback(
    (v: SearchVehicle) => {
      productTelemetryService.recordEvent('search', 'search.vehicle_opened', {
        elementId: 'explore.vehicle-card',
        elementType: 'vehicle_card',
        metadata: {
          source: 'explore',
          hasLocation: Boolean(location),
          activeFilterCount,
        },
      })
      router.push(`/explore/${v.id}` as never)
    },
    [activeFilterCount, location, router],
  )
  const handleSearchSubmit = (searchQuery: string) => {
    productTelemetryService.recordEvent('search', 'search.submitted', {
      elementId: 'explore.search-block',
      elementType: 'search',
      metadata: {
        category: category ?? 'all',
        activeFilterCount,
        hasQuery: Boolean(searchQuery.trim()),
      },
    })
    setQuery(searchQuery)
    void refetch()
  }
  const handleOpenLocationModal = useCallback(() => setLocationModalVisible(true), [])
  const handleCloseLocationModal = useCallback(() => setLocationModalVisible(false), [])
  const handleOpenFilters = useCallback(() => {
    productTelemetryService.recordEvent('filter', 'filter.sheet_opened', {
      elementId: 'explore.filter-button',
      elementType: 'button',
      metadata: { activeFilterCount, category: category ?? 'all' },
    })
    setFilterSheetOpen(true)
  }, [activeFilterCount, category, setFilterSheetOpen])
  const handleOpenAllVehicles = useCallback(() => router.push('/explore/all-vehicles'), [router])
  const handleOpenMap = useCallback(() => {
    productTelemetryService.recordEvent('search', 'search.map_opened', {
      elementId: 'explore.map-preview',
      elementType: 'map_preview',
      metadata: { resultCount: vehicles.length, surface: isTablet ? 'tablet' : 'mobile' },
    })
    router.push('/(tabs)/explore/map' as never)
  }, [isTablet, router, vehicles.length])
  const handleVehicleIdPress = useCallback(
    (id: string) => router.push(`/explore/${id}` as never),
    [router],
  )
  const wizardGate = useListingWizardGate()
  const handleHostCtaPress = wizardGate.open
  const handleToggleFavorite = useCallback(
    (vehicleId: string) => toggleFavorite(vehicleId),
    [toggleFavorite],
  )
  const handleExploreScroll = useProductTelemetryScroll('explore.mobile.scroll')
  const handleRetry = useCallback(() => {
    void refetch()
  }, [refetch])

  // STANDARDS §2: never repeat invariant chrome between sibling returns. The
  // error state used to be its own top-level return, which wiped the header,
  // the search bar, the categories and the filter sheet — and remounted the
  // whole screen (losing scroll position and every open modal) the moment one
  // request failed. Now only the results body varies.
  const showError = Boolean(error) && !isLoading && vehicles.length === 0
  const showIndependenceCampaign = isIndependenceCampaignBookingWindowOpen()

  // ═══════════════════════════════════════════════════════════════════════════
  // DESKTOP: Airbnb-style split screen (listings left, map right)
  // ═══════════════════════════════════════════════════════════════════════════
  if (isDesktop) {
    return (
      <LiquidCanvas>
        {/* Deriva del tema, no fijo: `style="light"` pintaba la barra de estado
            de blanco sobre `colors.background`, que en claro es claro. En un
            iPad en horizontal --que es donde entra este ramal-- la hora y la
            bateria se perdian. Mismo defecto que habia en (auth)/welcome.tsx. */}
        <StatusBar style={isDark ? 'light' : 'dark'} />
        <DesktopExploreLayout
          usingCountryFallback={usingCountryFallback}
          vehicles={vehicles as SearchVehicle[]}
          isLoading={isLoading}
          isRefetching={isRefetching}
          isSearching={isFetching}
          isError={showError}
          refetch={handleRetry}
          handleVehiclePress={handleVehiclePress}
          category={category}
          setCategory={setCategory}
          location={location}
          pad={pad}
          onLocationPress={handleOpenLocationModal}
          onSearchSubmit={handleSearchSubmit}
          onFilterPress={handleOpenFilters}
          activeFilterCount={activeFilterCount}
          query={query}
          initialFrame={mapInitialFrame}
        />
        <FilterSheet />
        <LocationSearchModal visible={locationModalVisible} onClose={handleCloseLocationModal} />
      </LiquidCanvas>
    )
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MOBILE / TABLET: Vertical scroll
  // ═══════════════════════════════════════════════════════════════════════════
  const gridCols = isTablet ? 2 : 0

  return (
    <LiquidCanvas>
      <StatusBar style={isDark ? 'light' : 'dark'} />
      <ScrollView
        showsVerticalScrollIndicator={false}
        onScroll={handleExploreScroll}
        scrollEventThrottle={64}
        refreshControl={
          <BrandedRefreshControl refreshing={isRefetching} onRefresh={() => refetch()} />
        }
        contentContainerStyle={{ paddingBottom: insets.bottom + SPACING.xl }}
      >
        {/* The greeting bar now lives in the global MobileTopNav (AppShell), as a
            flex sibling above this scene. The SPACING.md on the search block is
            what keeps the same rhythm under the bar. */}
        <View style={{ paddingHorizontal: pad }}>
          {/* Search */}
          <Animated.View
            collapsable={false}
            entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 2)}
            // Same step as the rest of the stack (search -> chips -> map are
            // all SPACING.md). At SPACING.sm this gap was less than half its
            // neighbours and the search bar read as stuck to the greeting bar.
            style={{ marginTop: SPACING.md }}
            testID="explore.search-block"
          >
            <SearchBlock
              location={location}
              onLocationPress={handleOpenLocationModal}
              onSubmit={handleSearchSubmit}
              resultCount={vehicles.length}
              isLoading={isFetching}
              query={query}
            />
          </Animated.View>

          {showIndependenceCampaign ? (
            <Animated.View
              collapsable={false}
              entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 3)}
              style={{ marginTop: SPACING.md }}
            >
              <IndependenceCampaignBanner onPress={handleOpenAllVehicles} />
            </Animated.View>
          ) : null}

          {/* Categories */}
          <Animated.View
            collapsable={false}
            entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 4)}
            style={{ marginTop: SPACING.md, marginHorizontal: -pad }}
            testID="explore.category-filter"
          >
            <CategoryFilter
              selected={category}
              onSelect={setCategory}
              desktop={isTablet}
              contentPadding={pad}
              onFilterPress={handleOpenFilters}
              activeFilterCount={activeFilterCount}
            />
          </Animated.View>

          {/* Map */}
          <Animated.View
            collapsable={false}
            entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 5)}
            style={{ marginTop: SPACING.md }}
            testID="explore.map-preview"
          >
            <DeferredMapPreview
              vehicles={vehicles as SearchVehicle[]}
              location={location}
              onViewMap={handleOpenMap}
              onVehiclePress={handleVehicleIdPress}
              height={exploreMapPreviewHeight}
              initialFrame={mapInitialFrame}
            />
          </Animated.View>

          {/* Nearby */}
          <Animated.View
            collapsable={false}
            entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 6)}
            style={{ marginTop: SPACING.lg }}
          >
            <SectionHeader
              title={t(scopeTitleKey)}
              subtitle={showError ? undefined : resultSubtitle(vehicles.length)}
              subtitleTestID="explore.result-status"
              onViewAll={handleOpenAllVehicles}
              colors={colors}
            />
            {isLoading ? (
              // The placeholder has to be the shape of what replaces it. On
              // tablet the results are a two-column grid of desktop cards, so
              // a row of phone-width carousel skeletons was the wrong shape.
              gridCols > 0 ? (
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.md }}>
                  {[0, 1, 2, 3].map((placeholder) => (
                    <View key={placeholder} style={tabletTileStyle(width)}>
                      <VehicleCard.Skeleton desktop />
                    </View>
                  ))}
                </View>
              ) : (
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  // Same edge rule as the carousel it stands in for.
                  style={{ marginHorizontal: -pad }}
                  contentContainerStyle={{
                    gap: SPACING.sm + SPACING.xs,
                    paddingHorizontal: pad,
                    paddingBottom: SPACING.xs,
                  }}
                >
                  <VehicleCard.Skeleton mobileWidth={exploreVehicleCardWidth} />
                  <VehicleCard.Skeleton mobileWidth={exploreVehicleCardWidth} />
                  <VehicleCard.Skeleton mobileWidth={exploreVehicleCardWidth} />
                </ScrollView>
              )
            ) : showError ? (
              <EmptyState.OfflineError onRetry={handleRetry} />
            ) : vehicles.length === 0 ? (
              <ExploreNoResultsPanel colors={colors} onFilterPress={handleOpenFilters} />
            ) : gridCols > 0 ? (
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: SPACING.md }}>
                {vehicles.map((v, index) => (
                  <ExploreVehicleCardTile
                    key={v.id}
                    vehicle={v as SearchVehicle}
                    index={index}
                    isFavorite={favoriteIds.has(v.id)}
                    onVehiclePress={handleVehiclePress}
                    onToggleFavorite={handleToggleFavorite}
                    desktop
                    viewportWidth={width}
                  />
                ))}
              </View>
            ) : (
              <NearbyVehicleCarousel
                vehicles={vehicles as SearchVehicle[]}
                favoriteIds={favoriteIds}
                mobileCardWidth={exploreVehicleCardWidth ?? EXPLORE_MOBILE_CARD_MAX_WIDTH}
                contentPadding={pad}
                onVehiclePress={handleVehiclePress}
                onToggleFavorite={handleToggleFavorite}
              />
            )}
          </Animated.View>

          {/* Popular — "Populares" now ranks the same filtered set the list
              above shows, so an empty result set leaves nothing to rank. A
              bare "Populares" header over dead space read as a broken
              section, so the whole block goes with it. */}
          {isLoading || popularVehicles.length > 0 ? (
            <Animated.View
              collapsable={false}
              entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 7)}
              style={{ marginTop: SPACING.lg }}
            >
              <SectionHeader
                title={t('explore.popular_title')}
                onViewAll={handleOpenAllVehicles}
                colors={colors}
              />
              <View style={{ gap: SPACING.sm }}>
                {isLoading ? (
                  <>
                    <VehicleListItem.Skeleton />
                    <VehicleListItem.Skeleton />
                    <VehicleListItem.Skeleton />
                  </>
                ) : (
                  popularVehicles.map((v, index) => (
                    <ExploreVehicleListTile
                      key={v.id}
                      vehicle={v as SearchVehicle}
                      index={index}
                      onVehiclePress={handleVehiclePress}
                      testPrefix="explore.popular-vehicle-card"
                    />
                  ))
                )}
              </View>
            </Animated.View>
          ) : null}

          <ResumeDraftDialog {...wizardGate.dialogProps} testIDPrefix="explore" />

          {/* Host CTA */}
          <Animated.View
            collapsable={false}
            entering={FadeInDown.duration(DURATION.normal).delay(STAGGER * 8)}
            style={{ marginTop: SPACING.md }}
          >
            <Pressable
              onPress={handleHostCtaPress}
              accessibilityRole="button"
              accessibilityLabel={t('explore.host_cta_title')}
              testID="explore.host-cta"
              style={{
                padding: SPACING.md - RADIUS.xs,
                borderRadius: RADIUS.lg,
                backgroundColor: colors.surface,
                flexDirection: 'row',
                alignItems: 'center',
                gap: SPACING.sm + SPACING.xs,
                ...WEB_SHADOWS.sm,
              }}
            >
              <LinearGradient
                colors={[COLORS.purple.DEFAULT, COLORS.amber.DEFAULT]}
                style={{
                  width: 42,
                  height: 42,
                  borderRadius: RADIUS.md + 1,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Car size={22} color="#FFFFFF" strokeWidth={1.8} />
              </LinearGradient>
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: colors.textPrimary, fontFamily: FONTS.body }}>
                  {t('explore.host_cta_title')}
                </Text>
                <Text
                  style={{
                    fontSize: 13,
                    color: colors.textSecondary,
                    marginTop: 2,
                    fontFamily: FONTS.body,
                  }}
                >
                  {t('explore.host_cta_subtitle')}
                </Text>
              </View>
              <ChevronRight size={14} color={COLORS.purple.DEFAULT} strokeWidth={2.5} />
            </Pressable>
          </Animated.View>
        </View>
      </ScrollView>
      <FilterSheet />
      <LocationSearchModal visible={locationModalVisible} onClose={handleCloseLocationModal} />
    </LiquidCanvas>
  )
}
